"use client";

import {
  Anchor,
  Box,
  Burger,
  Button,
  Container,
  Group,
  Transition,
  UnstyledButton,
} from "@mantine/core";
import { useRef } from "react";

import { useDisclosure } from "@mantine/hooks";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";

import { DARK_HEADER_PATH } from "@/app/(marketing)/constants";
import { useHeaderOverDarkBand } from "@/app/(marketing)/hooks/useHeaderOverDarkBand";
import { useNavLinks } from "@/app/(marketing)/hooks/useNavLinks";

import classes from "./Header.module.css";

const MOBILE_MENU_ID = "marketing-mobile-menu";

/** Whether a nav link points at the page the reader is on. A link without an
    href yet (Talk to a human, before its mailto is built) points nowhere. */
function isCurrent(pathname: string, href: string | undefined): boolean {
  return href !== undefined && pathname.startsWith(href);
}

/** Below `sm` the nav collapses into this panel, hung from the header's bottom
    edge so it sits right under the header wherever the header is. */
function MobileMenu({ opened, close }: { opened: boolean; close: () => void }) {
  const pathname = usePathname();
  const navLinks = useNavLinks();
  const links = [{ label: "Home", href: "/", component: Link, onClick: undefined }, ...navLinks];

  return (
    <Transition mounted={opened} transition="fade-down" duration={150}>
      {(styles) => (
        <Box
          id={MOBILE_MENU_ID}
          component="nav"
          aria-label="Site"
          className={classes.mobileMenu}
          hiddenFrom="sm"
          style={styles}
        >
          {links.map((link) => {
            const isActive = link.href === "/" ? pathname === "/" : isCurrent(pathname, link.href);

            return (
              <UnstyledButton
                className={classes.mobileLink}
                data-active={isActive || undefined}
                component={link.component}
                href={link.href}
                key={link.label}
                onClick={() => {
                  link.onClick?.();
                  close();
                }}
              >
                {link.label}
              </UnstyledButton>
            );
          })}
        </Box>
      )}
    </Transition>
  );
}

/**
 * The marketing chrome: a header pinned to the top of every page. It sits in
 * the page's flow, so it is sticky rather than fixed and anything pinned below
 * it offsets by `--marketing-header-height`. It wears a dark band's color while one is under it, and is
 * white everywhere else; only the homepage has dark bands today.
 */
export default function Header() {
  const pathname = usePathname();
  const navLinks = useNavLinks();
  const [opened, { toggle, close }] = useDisclosure(false);
  const headerRef = useRef<HTMLElement>(null);
  const band = useHeaderOverDarkBand(pathname, headerRef);
  const isDark = band.isOverDarkBand ?? pathname === DARK_HEADER_PATH;

  // The row fills the header height so its children center vertically; the
  // logo is sized to its artwork and does not prop the row open.
  return (
    <Box
      component="header"
      ref={headerRef}
      className={classes.header}
      data-dark={isDark || undefined}
      data-tone-settled={band.isSettled || undefined}
    >
      <Container size="lg" h="100%">
        <Group justify="space-between" h="100%">
          <Group gap="xs">
            <Burger
              opened={opened}
              onClick={toggle}
              hiddenFrom="sm"
              size="sm"
              color={isDark ? "white" : undefined}
              aria-label="Toggle navigation"
              aria-expanded={opened}
              aria-controls={MOBILE_MENU_ID}
            />
            {/* Both artworks stay mounted and crossfade, since an image's
                source cannot transition. */}
            <Link href="/" className={classes.logo}>
              <Image
                className={classes.logoOnLight}
                height={25}
                width={116}
                src="/logo/logo.svg"
                alt="Orgtools Logo"
              />
              <Image
                className={classes.logoOnDark}
                height={25}
                width={116}
                src="/logo/logo-light.svg"
                alt=""
              />
            </Link>
          </Group>
          <Group visibleFrom="sm">
            {navLinks.map((link) =>
              link.isCta ? (
                <Button
                  component={link.component}
                  href={link.href}
                  key={link.label}
                  size="compact-sm"
                  onClick={link.onClick}
                  {...link.buttonProps}
                >
                  {link.label}
                </Button>
              ) : (
                <Anchor
                  className={`${classes.navLink}${isCurrent(pathname, link.href) ? ` ${classes.navLinkActive}` : ""}`}
                  component={link.component}
                  href={link.href}
                  key={link.label}
                  onClick={link.onClick}
                  underline="never"
                >
                  {link.label}
                </Anchor>
              ),
            )}
          </Group>
        </Group>
      </Container>
      <MobileMenu opened={opened} close={close} />
    </Box>
  );
}
